/**
 * Web speech-to-text — the on-device WASM Whisper engine.
 *
 * This is the SAME engine that ships to production: recognition runs fully in the
 * browser via `local-web-stt.web.ts`, audio never leaves the device, and it's
 * gated by the voice feature flag (`VOICE_ENABLED`) + browser capability.
 *
 * The old dev-only cloud Web Speech shim was removed: it streamed audio to a
 * vendor (never the production behaviour) and, once the flag stopped being
 * `__DEV__`-gated, would have been able to run in prod. Web STT is now local-only;
 * where the local engine isn't available it fails closed to text.
 */
import {
  LOCAL_WEB_STT_AVAILABLE,
  LOCAL_WEB_STT_LABEL,
  onLocalSttPrepareProgress,
  prewarmLocalWebStt,
  probeLocalSttAssets,
  releaseLocalWebStt,
  startLocalWebStt,
} from "./local-web-stt.web";
import type { SttHandle, SttHandlers } from "./stt-types";

export const STT_SUPPORTED = LOCAL_WEB_STT_AVAILABLE;

/**
 * The async half of `STT_SUPPORTED`: browser primitives can all exist while the
 * self-hosted model assets are not served (today's deployed web). Voice must
 * not be offered until this resolves true — fail closed, not fail-on-tap.
 */
export const probeSttAssets = probeLocalSttAssets;
export const STT_LABEL = LOCAL_WEB_STT_LABEL;

// The web worker runs its own energy VAD and emits `onEnd` when it detects
// end-of-utterance, so the hook must NOT layer its own silence/acoustic timer on
// top (that would race the worker). Native has no such VAD and sets this false.
export const STT_OWNS_ENDPOINTING = true;

export type { SttHandle, SttHandlers } from "./stt-types";

/** Start STT. Returns null when the on-device engine isn't available (fail closed). */
export function startStt(handlers: SttHandlers): SttHandle | null {
  return startLocalWebStt(handlers);
}

/** Warm the mic ahead of the first turn so capture starts instantly. */
export async function prewarmStt(): Promise<void> {
  if (LOCAL_WEB_STT_AVAILABLE) await prewarmLocalWebStt();
}

/** Release any warmed mic resources when leaving voice mode / unmounting. */
export function releaseStt(): void {
  if (LOCAL_WEB_STT_AVAILABLE) releaseLocalWebStt();
}

/**
 * Model-download progress (0-99, null when not downloading). Loud only on a
 * browser's first visit — the model assets are served immutable — but that
 * first visit is a ~77 MB wait behind an otherwise mute status line.
 */
export const onSttPrepareProgress = onLocalSttPrepareProgress;
